import React, { useState, useEffect, useCallback } from 'react';
import { getAdminUsers, getUser, updateUser, createAdminUser } from '../services/adminUserService';
import { getActivityLog, logActivity } from '../services/adminActivityService';
import AdminAuthService from '../services/adminAuthService';
import { can, getRoleDisplayName, ADMIN_ROLES, getCreatableAdminRoles, canAssignAdminRole, getViewableAdminRoles, canViewAdminRole } from '../utils/adminPermissions';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { USERS_TABLE_ID } from '../lib/appwrite';
import './AdminUsersPage.css';

const formatAddress = (addr) => {
  if (!addr) return '—';
  if (Array.isArray(addr)) return addr.filter(Boolean).join(', ') || '—';
  return String(addr);
};

const formatDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [adminRole, setAdminRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userActivities, setUserActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [editRole, setEditRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('view'); // view | create
  const [createForm, setCreateForm] = useState({
    fname: '',
    lname: '',
    email: '',
    password: '',
    phoneNumber: '',
    roles: '',
  });

  const creatableRoles = adminRole ? getCreatableAdminRoles(adminRole) : [];
  const viewableRoles = adminRole ? getViewableAdminRoles(adminRole) : [];
  const canCreateAdmin =
    creatableRoles.length > 0 &&
    (can(adminRole, 'create', 'adminUsers') || can(adminRole, 'role', 'adminUsers'));

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersRes, role] = await Promise.all([
        getAdminUsers(),
        AdminAuthService.getAdminRole(),
      ]);
      setUsers(usersRes || []);
      setAdminRole(role);
    } catch (err) {
      const msg = err.message || 'Failed to load admin users';
      const isAuthError =
        typeof msg === 'string' &&
        (msg.toLowerCase().includes('not authorized') || msg.toLowerCase().includes('unauthorized'));
      setError(
        isAuthError
          ? `${msg} Add REACT_APP_APPWRITE_API_KEY to .env for server-level access.`
          : msg
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [USERS_TABLE_ID] });

  useEffect(() => {
    if (selectedUser && adminRole && !canViewAdminRole(adminRole, selectedUser.roles || selectedUser.role)) {
      setSelectedUser(null);
    }
  }, [adminRole, selectedUser]);

  const filteredUsers = users
    .filter((u) => {
      const roleVal = (u.roles || u.role || '').toLowerCase().trim();
      return canViewAdminRole(adminRole, roleVal);
    })
    .filter((u) => {
      const name = [u.fname, u.lname].filter(Boolean).join(' ').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q || name.includes(q) || email.includes(q) || (u.phoneNumber || '').toLowerCase().includes(q);
      const roleVal = (u.roles || u.role || '').toLowerCase().trim();
      const matchRole = !filterRole || roleVal === filterRole.toLowerCase();
      return matchSearch && matchRole;
    });

  const canUpdateRole = adminRole && (can(adminRole, 'update', 'adminUsers') || can(adminRole, 'role', 'adminUsers'));
  const assignableRoles = ADMIN_ROLES.filter((r) => canAssignAdminRole(adminRole, r));
  const canEditSelectedRole =
    canUpdateRole &&
    selectedUser &&
    canViewAdminRole(adminRole, selectedUser.roles || selectedUser.role) &&
    canAssignAdminRole(adminRole, selectedUser.roles || selectedUser.role);

  const handleSaveRole = async () => {
    if (!selectedUser || !editRole) return;
    setSaving(true);
    try {
      await updateUser(selectedUser.$id, { roles: editRole, role: editRole });
      await logActivity({
        action: 'update',
        resourceType: 'admin_user',
        resourceId: selectedUser.$id,
        details: `Updated admin role to ${editRole}`,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.$id === selectedUser.$id ? { ...u, roles: editRole, role: editRole } : u
        )
      );
      setSelectedUser((prev) => (prev ? { ...prev, roles: editRole, role: editRole } : null));
      setEditRole('');
    } catch (err) {
      setError(err.message || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (user) => {
    setSelectedUser(user);
    setEditRole((user.roles || user.role || '').toLowerCase().trim());
    setUserDetails(null);
    setUserActivities([]);
    setMode('view');
  };

  const openCreate = () => {
    setMode('create');
    setCreateForm({
      fname: '',
      lname: '',
      email: '',
      password: '',
      phoneNumber: '',
      roles: creatableRoles[0] || '',
    });
  };

  const closeCreate = () => {
    setMode('view');
  };

  const handleCreateAdmin = async () => {
    const role = (createForm.roles || '').toLowerCase().trim();
    if (!role || !creatableRoles.includes(role)) {
      setError('Select a valid role');
      return;
    }
    if (!createForm.email?.trim()) {
      setError('Email is required');
      return;
    }
    const pwd = (createForm.password || '').trim();
    if (pwd.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createAdminUser({
        fname: createForm.fname?.trim() || '',
        lname: createForm.lname?.trim() || '',
        email: createForm.email?.trim(),
        password: pwd,
        phoneNumber: createForm.phoneNumber?.trim() || '',
        roles: role,
        role,
      });
      await logActivity({
        action: 'create',
        resourceType: 'admin_user',
        resourceId: created.$id,
        details: `Added ${getRoleDisplayName(role)}: ${createForm.email}`,
      });
      setUsers((prev) => [...prev, { ...created, roles: role, role }]);
      closeCreate();
    } catch (err) {
      setError(err.message || 'Failed to add admin');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!selectedUser?.$id) return;
    let cancelled = false;
    const load = async () => {
      setActivitiesLoading(true);
      try {
        const [detailsRes, activitiesRes] = await Promise.all([
          getUser(selectedUser.$id).catch(() => selectedUser),
          getActivityLog({ adminUserId: selectedUser.$id, timeRange: 'year', limit: 100 }),
        ]);
        if (!cancelled) {
          setUserDetails(detailsRes);
          setUserActivities(activitiesRes || []);
        }
      } catch {
        if (!cancelled) setUserActivities([]);
      } finally {
        if (!cancelled) setActivitiesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedUser?.$id]);

  if (loading && users.length === 0) {
    return (
      <div className="admin-users-loading">
        <div className="dashboard-spinner" />
        <p>Loading admin users...</p>
      </div>
    );
  }

  return (
    <div className="admin-users-page">
      <div className="admin-users-header">
        <div>
          <h1 className="dashboard-title">Admin Users</h1>
          <p className="dashboard-subtitle">
            Manage users who can access the admin panel.
          </p>
        </div>
        {canCreateAdmin && (
          <button type="button" className="admin-users-btn-add" onClick={openCreate}>
            + Add Admin
          </button>
        )}
      </div>

      {error && (
        <div className="admin-users-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="admin-users-toolbar">
        <div className="admin-users-search-wrap">
          <img src="/icons/Search.svg" alt="" className="admin-users-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="Search by name, email, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="admin-users-search"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="admin-users-filter"
        >
          <option value="">All roles</option>
          {viewableRoles.map((r) => (
            <option key={r} value={r}>
              {getRoleDisplayName(r)}
            </option>
          ))}
        </select>
        <button type="button" className="admin-users-btn-refresh" onClick={loadData}>
          ⟳ Refresh
        </button>
      </div>

      <div className="admin-users-table-wrap">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={4} className="admin-users-empty">
                  No admin users found.
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr
                  key={u.$id}
                  className="admin-users-row-clickable"
                  onClick={() => openDetail(u)}
                >
                  <td><strong>{[u.fname, u.lname].filter(Boolean).join(' ') || '—'}</strong></td>
                  <td>{u.email || '—'}</td>
                  <td>{u.phoneNumber || u.phone || '—'}</td>
                  <td>
                    <span className={`admin-users-role admin-users-role-${(u.roles || u.role || '').toLowerCase()}`}>
                      {getRoleDisplayName(u.roles || u.role)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedUser && (
        <div className="admin-users-modal-overlay" onClick={() => setSelectedUser(null)} role="dialog" aria-modal="true">
          <div className="admin-users-modal admin-users-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="admin-users-modal-header">
              <h2>Admin User Details</h2>
              <button type="button" className="admin-users-modal-close" onClick={() => setSelectedUser(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="admin-users-modal-body">
              <div className="admin-users-modal-section">
                <h3>Profile</h3>
                <div className="admin-users-modal-grid">
                  <div className="admin-users-modal-field">
                    <label>Name</label>
                    <span>{[selectedUser.fname, selectedUser.lname].filter(Boolean).join(' ') || '—'}</span>
                  </div>
                  <div className="admin-users-modal-field">
                    <label>Email</label>
                    <span>
                      <a href={`mailto:${selectedUser.email}`}>{selectedUser.email || '—'}</a>
                    </span>
                  </div>
                  <div className="admin-users-modal-field">
                    <label>Phone</label>
                    <span>
                      <a href={`tel:${selectedUser.phoneNumber || selectedUser.phone}`}>
                        {selectedUser.phoneNumber || selectedUser.phone || '—'}
                      </a>
                    </span>
                  </div>
                  <div className="admin-users-modal-field">
                    <label>User ID</label>
                    <span className="admin-users-modal-mono">{selectedUser.$id}</span>
                  </div>
                  <div className="admin-users-modal-field admin-users-modal-field-wide">
                    <label>UID</label>
                    <span className="admin-users-modal-mono">{(userDetails || selectedUser).uid || '—'}</span>
                  </div>
                  {(userDetails || selectedUser).address && (
                    <div className="admin-users-modal-field admin-users-modal-field-wide">
                      <label>Address</label>
                      <span>{formatAddress((userDetails || selectedUser).address)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="admin-users-modal-section">
                <h3>Role</h3>
                {canEditSelectedRole ? (
                  <div className="admin-users-modal-role-edit">
                    <select
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                      className="admin-users-modal-select"
                    >
                      {assignableRoles.map((r) => (
                        <option key={r} value={r}>
                          {getRoleDisplayName(r)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="admin-users-modal-btn-save"
                      onClick={handleSaveRole}
                      disabled={saving || editRole === (selectedUser.roles || selectedUser.role || '').toLowerCase()}
                    >
                      {saving ? 'Saving…' : 'Save role'}
                    </button>
                  </div>
                ) : (
                  <span className={`admin-users-role admin-users-role-${(selectedUser.roles || selectedUser.role || '').toLowerCase()}`}>
                    {getRoleDisplayName(selectedUser.roles || selectedUser.role)}
                  </span>
                )}
              </div>
              <div className="admin-users-modal-section">
                <h3>Recent Activity ({userActivities.length})</h3>
                {activitiesLoading ? (
                  <p className="admin-users-modal-loading">Loading activities…</p>
                ) : (
                  <div className="admin-users-activities-wrap">
                    {userActivities.length === 0 ? (
                      <p className="admin-users-activities-empty">No activity recorded.</p>
                    ) : (
                      <table className="admin-users-activities-table">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Action</th>
                            <th>Resource</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userActivities.map((a) => (
                            <tr key={a.$id}>
                              <td>{formatDateTime(a.createdAt)}</td>
                              <td>
                                <span className={`admin-users-activity-action admin-users-activity-action-${(a.action || '').toLowerCase()}`}>
                                  {a.action}
                                </span>
                              </td>
                              <td>{a.resourceType || '—'}</td>
                              <td className="admin-users-activity-details">{a.details || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {mode === 'create' && (
        <div className="admin-users-modal-overlay" onClick={closeCreate} role="dialog" aria-modal="true">
          <div className="admin-users-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-users-modal-header">
              <h2>Add Admin</h2>
              <button type="button" className="admin-users-modal-close" onClick={closeCreate} aria-label="Close">
                ×
              </button>
            </div>
            <div className="admin-users-modal-body">
              <div className="admin-users-modal-section">
                <div className="admin-users-create-grid">
                  <div className="admin-users-modal-field">
                    <label>First name</label>
                    <input
                      type="text"
                      value={createForm.fname}
                      onChange={(e) => setCreateForm((f) => ({ ...f, fname: e.target.value }))}
                      placeholder="Jane"
                      className="admin-users-input"
                    />
                  </div>
                  <div className="admin-users-modal-field">
                    <label>Last name</label>
                    <input
                      type="text"
                      value={createForm.lname}
                      onChange={(e) => setCreateForm((f) => ({ ...f, lname: e.target.value }))}
                      placeholder="Doe"
                      className="admin-users-input"
                    />
                  </div>
                  <div className="admin-users-modal-field admin-users-modal-field-wide">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="jane@example.com"
                      className="admin-users-input"
                      required
                    />
                  </div>
                  <div className="admin-users-modal-field admin-users-modal-field-wide">
                    <label>Password *</label>
                    <input
                      type="password"
                      value={createForm.password}
                      onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                      placeholder="Min 8 characters"
                      className="admin-users-input"
                      minLength={8}
                      required
                    />
                    <span className="admin-users-field-hint">Share this password securely — credentials are not emailed</span>
                  </div>
                  <div className="admin-users-modal-field">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={createForm.phoneNumber}
                      onChange={(e) => setCreateForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                      placeholder="+1234567890"
                      className="admin-users-input"
                    />
                  </div>
                  <div className="admin-users-modal-field">
                    <label>Role</label>
                    <select
                      value={createForm.roles}
                      onChange={(e) => setCreateForm((f) => ({ ...f, roles: e.target.value }))}
                      className="admin-users-input"
                    >
                      {creatableRoles.map((r) => (
                        <option key={r} value={r}>
                          {getRoleDisplayName(r)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="admin-users-modal-role-edit">
                <button
                  type="button"
                  className="admin-users-modal-btn-save"
                  onClick={handleCreateAdmin}
                  disabled={saving || !createForm.email?.trim() || !(createForm.password || '').trim()}
                >
                  {saving ? 'Adding…' : 'Add Admin'}
                </button>
                <button type="button" className="admin-users-btn-cancel" onClick={closeCreate}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
